import type { ButtonHTMLAttributes } from 'react'
import type { CampaignIcon } from 'shared/ui/StackIcon/keys'

import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import ModalWindow from 'components/molecules/ModalWindow'
import { CONSTS } from 'config/objectConst'
import { notificationHooks } from 'features/notifications'
import { atom } from 'nanostores'
import { useNavigate } from 'react-router-dom'
import { appConnector } from 'shared/connector'
import { EMPTY_CONFIRM } from 'shared/odata/confirm'
import { StackIcon } from 'shared/ui'

import type { Actions, Campaign } from '../../types'

import { api } from '../../api'
import css from './style.module.css'

interface ConfirmDialog {
	confirmHandler: () => void
	content: string
	title: string
}

const $confirmDialog = atom<ConfirmDialog | null>(null)

const initialErrorModal = {
	...EMPTY_CONFIRM,
	// biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
	startCampaign: () => {}
}
const $errorModal = atom(initialErrorModal)
const closeConfirm = () => $errorModal.set(initialErrorModal)

const closeModal = () => $confirmDialog.set(null)

Actions.Modal = () => [<ConfirmModal key='1' />, <TableModal key='2' />]

interface WithIDRemove {
	removeIdFromSelected: (id: string) => void
}

export function Actions({
	removeIdFromSelected,

	...campaign
}: Campaign & WithIDRemove) {
	const { actions, id, state } = campaign

	const isArchiveFlow = actions.unarchive.enabled

	if (isArchiveFlow) {
		return (
			<ButtonUnArchive
				actions={actions}
				id={id}
				removeIdFromSelected={removeIdFromSelected}
			/>
		)
	}

	return (
		<div
			className={clsx(css.actions, 'group-position')}
			style={{ position: 'absolute' }}
		>
			<ButtonRun actions={actions} id={id} state={state} />
			<ButtonStop actions={actions} id={id} />
			<ButtonComplete actions={actions} id={id} />
			<ButtonClone actions={actions} id={id} />
			<ButtonArchive
				actions={actions}
				id={id}
				removeIdFromSelected={removeIdFromSelected}
			/>
		</div>
	)
}

export function ConfirmModal() {
	const confirmState = useStore($errorModal)

	function close() {
		closeConfirm()
		closeModal()
	}

	return (
		<ModalWindow
			classes='confirmation-launch-modal'
			closeHandler={close}
			open={confirmState.status}
		>
			<ConfirmationDialog
				closeHandler={close}
				confirmationDialog={confirmState}
				startCampaign={confirmState.startCampaign}
			/>
		</ModalWindow>
	)
}

const EMPTY_STATE: ConfirmDialog = {
	confirmHandler: console.log,
	content: '',
	title: ''
}

export function TableModal() {
	const props = useStore($confirmDialog)

	return (
		<ModalWindow
			closeHandler={closeModal}
			closeText='No'
			confirmText='Yes'
			{...(props ?? EMPTY_STATE)}
			open={props !== null}
		/>
	)
}

function ActionButton({
	actionKey,
	actions,
	disabled,
	onClick
}: Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled' | 'onClick'> &
	Pick<Campaign, 'actions'> & { actionKey: keyof Actions }) {
	return (
		// <TooltipCustom arrow={true} title={actions[actionKey].hint}>
		<button
			aria-label={actions[actionKey].hint}
			className={css.action}
			disabled={!actions[actionKey].enabled || disabled}
			onClick={onClick}
			title={actions[actionKey].hint}
			type='button'
		>
			<StackIcon icon={actionKey as CampaignIcon} stack='campaign' width={19} />
		</button>
		// </TooltipCustom>
	)
}

function ButtonArchive({
	actions,
	id,
	removeIdFromSelected
}: Pick<Campaign, 'actions' | 'id'> & WithIDRemove) {
	const snack = notificationHooks.useSnack()
	const [sendRequest, { isLoading, originalArgs }] =
		api.useArchiveCampaignMutation()
	const isDisabled = originalArgs === id && isLoading

	const handleClick = () =>
		$confirmDialog.set({
			...CONSTS.confirmationDialogs['1.7'],
			async confirmHandler() {
				closeModal()
				const { data } = await sendRequest(id)

				if (data) {
					removeIdFromSelected(id)
					snack('3.10')
				}
			}
		})

	return (
		<ActionButton
			actionKey='archive'
			actions={actions}
			disabled={isDisabled}
			onClick={handleClick}
		/>
	)
}

function ButtonClone({ actions, id }: Pick<Campaign, 'actions' | 'id'>) {
	const snack = notificationHooks.useSnack()
	const [sendRequest, { isLoading, originalArgs }] =
		api.useCloneCampaignMutation()
	const isDisabled = originalArgs === id && isLoading

	const navigate = useNavigate()
	const handleClick = () =>
		$confirmDialog.set({
			...CONSTS.confirmationDialogs['1.10'],
			async confirmHandler() {
				closeModal()
				const { data: campaign } = await sendRequest(id)

				if (campaign) {
					snack('3.21')
					navigate(`/campaigns/${campaign.id}/tune`)
				}
			}
		})

	return (
		<ActionButton
			actionKey='clone'
			actions={actions}
			disabled={isDisabled}
			onClick={handleClick}
		/>
	)
}

function ButtonComplete({ actions, id }: Pick<Campaign, 'actions' | 'id'>) {
	const snack = notificationHooks.useSnack()
	const [sendRequest, { isLoading, originalArgs }] =
		api.useCompleteCampaignMutation()
	const isDisabled = originalArgs === id && isLoading

	const handleClick = () =>
		$confirmDialog.set({
			...CONSTS.confirmationDialogs['1.2'],
			async confirmHandler() {
				closeModal()
				const { data } = await sendRequest(id)

				data && snack('3.8')
			}
		})
	return (
		<ActionButton
			actionKey='complete'
			actions={actions}
			disabled={isDisabled}
			onClick={handleClick}
		/>
	)
}

function ButtonRun({
	actions,
	id,
	state
}: Pick<Campaign, 'actions' | 'id' | 'state'>) {
	const snack = notificationHooks.useSnack()
	const [run, runOptions] = api.useRunCampaignMutation()
	const [getPreview, previewQuery] = api.useCampaignPreviewMutation()

	const isDisabled =
		(previewQuery.originalArgs === id && previewQuery.isLoading) ||
		(runOptions.isLoading && runOptions.originalArgs === id)

	const isStopped = state === 'Stopped'

	async function runCampaign() {
		const { data } = await run(id)

		if (data) {
			snack(isStopped ? '3.9' : '3.6')
			return appConnector.auth.refreshAccessToken()
		}
	}

	async function handleClick() {
		if (!actions.run.enabled) {
			return
		}

		if (isStopped) {
			return runCampaign()
		}

		const { data } = await getPreview(id)

		if (!data) {
			return
		}
		if (!data.validation_rules_summary.length) {
			const content = { ...CONSTS.confirmationDialogs['1.5'] }

			content.content = content.content.replace(
				'{contacts_count}',
				data.valid_audience_count.toString()
			)

			$confirmDialog.set({
				...content,
				async confirmHandler() {
					closeModal()
					return runCampaign()
				}
			})
		} else {
			$errorModal.set({
				data,
				id,
				startCampaign: runCampaign,
				status: true
			})
		}
	}

	return (
		<ActionButton
			actionKey='run'
			actions={actions}
			disabled={isDisabled}
			onClick={handleClick}
		/>
	)
}

function ButtonStop({ actions, id }: Pick<Campaign, 'actions' | 'id'>) {
	const snack = notificationHooks.useSnack()
	const [sendRequest, { isLoading, originalArgs }] =
		api.useStopCampaignMutation()
	const isDisabled = originalArgs === id && isLoading

	async function handleClick() {
		const { data } = await sendRequest(id)
		if (data) {
			appConnector.auth.refreshAccessToken()
			snack('3.7')
		}
	}
	return (
		<ActionButton
			actionKey='stop'
			actions={actions}
			disabled={isDisabled}
			onClick={handleClick}
		/>
	)
}

function ButtonUnArchive({
	actions,
	id,
	removeIdFromSelected
}: Pick<Campaign, 'actions' | 'id'> & WithIDRemove) {
	const [sendRequest, { isLoading, originalArgs }] =
		api.useUnArchiveCampaignMutation()
	const isDisabled = originalArgs === id && isLoading

	return (
		<div
			className={css.actions}
			style={{ position: 'absolute', right: '7px', top: '10px' }}
		>
			<ActionButton
				actionKey='unarchive'
				actions={actions}
				disabled={isDisabled}
				onClick={async () => {
					await sendRequest(id)
					removeIdFromSelected(id)
				}}
			/>
		</div>
	)
}
